import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Activity, AlertTriangle, ArrowLeft, Briefcase, Heart, Plane, RefreshCw, Shield, Wallet } from 'lucide-react';

const RISK_COLORS = {
  MILD: '#22C55E',
  GUARDED: '#4ADE80',
  ELEVATED: '#F59E0B',
  SEVERE: '#F97316',
  CRITICAL: '#EF4444'
};

const AREA_META = {
  MONEY: { key: 'money', label: 'Money', icon: Wallet },
  HEALTH: { key: 'health', label: 'Health', icon: Activity },
  RELATIONSHIP: { key: 'relationship', label: 'Relationship', icon: Heart },
  TRAVEL: { key: 'travel_accident', label: 'Travel & Accident', icon: Plane },
  CAREER: { key: 'career', label: 'Work & Career', icon: Briefcase }
};

const DAILY_ESSENTIAL_KEYS = [
  { key: 'cloth_color', label: 'Dress' },
  { key: 'breakfast', label: 'Breakfast' },
  { key: 'metal', label: 'Metal / Ring / Stone' },
  { key: 'charity', label: 'Charity' }
];

const SCORE_TONE = {
  EXCELLENT: { color: '#22C55E', bg: 'rgba(34,197,94,0.12)', label: 'Good' },
  GOOD: { color: '#22C55E', bg: 'rgba(34,197,94,0.12)', label: 'Good' },
  NEUTRAL: { color: '#60A5FA', bg: 'rgba(96,165,250,0.12)', label: 'Neutral' },
  CAUTION: { color: '#F59E0B', bg: 'rgba(245,158,11,0.12)', label: 'Alarming' },
  DANGER: { color: '#EF4444', bg: 'rgba(239,68,68,0.12)', label: 'Bad' }
};

const normalizeGuidance = (day) => day?.forecast_guidance || day?.guru_guidance || {};

export default function WeeklyInsights() {
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [activeDay, setActiveDay] = useState('Monday');

  const loadReport = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/insights/weekly-live');
      const days = res.data?.daily || [];
      const hasNewFields = days.every((d) => d.nakshatra_actions && d.kb_area_guidance);

      if (!hasNewFields) {
        await generateReport();
      } else {
        setReport(res.data);
        const todayName = new Date().toLocaleDateString('en-US', { weekday: 'long' });
        setActiveDay(todayName);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        await generateReport();
      } else {
        setError('Failed to load weekly forecast');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, []);

  const generateReport = async () => {
    setGenerating(true);
    setError('');
    try {
      const res = await axios.post('/api/insights/weekly-live');
      setReport(res.data);
      const todayName = new Date().toLocaleDateString('en-US', { weekday: 'long' });
      setActiveDay(todayName);
    } catch (err) {
      setError(err.response?.data?.detail || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const activeDayData = useMemo(() => {
    if (!report?.daily?.length) return null;
    return report.daily.find((d) => d.day_name === activeDay) || report.daily[0];
  }, [report, activeDay]);

  const guidance = normalizeGuidance(activeDayData);
  const riskLevel = guidance?.risk_level || activeDayData?.risk_level || 'GUARDED';
  const riskColor = RISK_COLORS[riskLevel] || '#F59E0B';

  if (loading || generating) {
    return (
      <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>
        <div style={{ textAlign: 'center' }}>
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}>
            <Shield size={56} color="#00D9FF" />
          </motion.div>
          <p style={{ color: '#E5E7EB', marginTop: '1.5rem', fontSize: '1.25rem', fontWeight: 600 }}>
            {generating ? 'Generating your weekly forecast...' : 'Loading weekly forecast...'}
          </p>
          <p style={{ color: '#6B7280', fontSize: '0.9rem', marginTop: '0.4rem' }}>
            Reading nakshatra, planetary movement and remedy signals
          </p>
        </div>
      </div>
    );
  }

  if (!report?.daily?.length || !activeDayData) {
    return (
      <div className="page">
        <div className="container" style={{ maxWidth: '740px', textAlign: 'center', paddingTop: '3rem' }}>
          {error && (
            <div data-testid="weekly-forecast-error" style={{ padding: '1rem', background: '#EF444420', border: '1px solid #EF4444', borderRadius: '8px', marginBottom: '1.5rem', color: '#EF4444' }}>
              {error}
            </div>
          )}
          <Shield size={56} color="#00D9FF" style={{ margin: '0 auto 1.5rem' }} />
          <h2 style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>Weekly Forecast</h2>
          <p style={{ color: '#9CA3AF', marginBottom: '2rem' }}>Daily warning + remedy planner for the week</p>
          <button data-testid="generate-weekly-forecast-button" onClick={generateReport} className="btn btn-primary" style={{ fontSize: '1.125rem', padding: '1rem 2rem' }}>
            Generate This Week
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="container" style={{ maxWidth: '1100px' }}>
        <button data-testid="weekly-back-to-dashboard" onClick={() => navigate('/dashboard')} className="btn btn-ghost" style={{ marginBottom: '1rem' }}>
          <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} /> Dashboard
        </button>

        <div style={{ textAlign: 'center', marginBottom: '1.2rem' }}>
          <h1 data-testid="weekly-title" style={{ fontSize: '1.9rem', fontWeight: 800, marginBottom: '0.2rem' }}>Weekly Forecast</h1>
          <p data-testid="weekly-header-metadata" style={{ color: '#6B7280', fontSize: '0.9rem' }}>
            {report.week_start} to {report.week_end} · {report.current_dasha?.planet} Mahadasha · Driver {report.numerology?.driver} · Conductor {report.numerology?.conductor}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <div style={{ padding: '0.7rem 1rem', background: '#10B98118', border: '1px solid #10B98144', borderRadius: '10px', textAlign: 'center' }}>
            <p data-testid="weekly-best-day" style={{ fontSize: '0.96rem', fontWeight: 700, color: '#22C55E' }}>{report.best_day?.name}</p>
            <p style={{ color: '#6B7280', fontSize: '0.75rem' }}>Best Day</p>
          </div>
          <div style={{ padding: '0.7rem 1rem', background: '#EF444418', border: '1px solid #EF444450', borderRadius: '10px', textAlign: 'center' }}>
            <p data-testid="weekly-worst-day" style={{ fontSize: '0.96rem', fontWeight: 700, color: '#EF4444' }}>{report.worst_day?.name}</p>
            <p style={{ color: '#6B7280', fontSize: '0.75rem' }}>Most Cautious Day</p>
          </div>
        </div>

        <div data-testid="weekly-day-tabs" style={{ display: 'flex', gap: '0.55rem', overflowX: 'auto', paddingBottom: '0.5rem', marginBottom: '1rem' }}>
          {report.daily.map((day) => {
            const isActive = activeDayData.day_name === day.day_name;
            const dayRisk = day.risk_level || 'GUARDED';
            const dayRiskColor = RISK_COLORS[dayRisk] || '#F59E0B';
            return (
              <button
                key={day.day_name}
                data-testid={`weekly-day-tab-${day.day_name.toLowerCase()}`}
                onClick={() => setActiveDay(day.day_name)}
                style={{
                  minWidth: '130px',
                  padding: '0.55rem 0.7rem',
                  borderRadius: '12px',
                  border: isActive ? `2px solid ${dayRiskColor}` : '1px solid rgba(255,255,255,0.12)',
                  background: isActive ? `${dayRiskColor}16` : 'rgba(15,17,40,0.8)',
                  color: '#E5E7EB',
                  cursor: 'pointer'
                }}
              >
                <p style={{ fontSize: '0.88rem', fontWeight: 700 }}>{day.day_name}</p>
                <p style={{ fontSize: '0.72rem', color: '#9CA3AF' }}>{day.date}</p>
              </button>
            );
          })}
        </div>

        <div data-testid="weekly-active-day-summary" style={{ border: '1px solid rgba(96,165,250,0.25)', background: 'rgba(96,165,250,0.08)', borderRadius: '12px', padding: '1rem', marginBottom: '0.9rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.8rem', flexWrap: 'wrap', marginBottom: '0.7rem' }}>
            <h3 data-testid="active-day-heading" style={{ fontSize: '1.15rem', fontWeight: 700 }}>{activeDayData.day_name}</h3>
            <span data-testid="active-day-risk-level" style={{ color: riskColor, border: `1px solid ${riskColor}77`, background: `${riskColor}14`, borderRadius: '999px', padding: '0.25rem 0.7rem', fontWeight: 700, fontSize: '0.78rem' }}>
              {riskLevel}
            </span>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '0.5rem', fontSize: '0.86rem' }}>
            <p data-testid="active-day-nakshatra"><strong style={{ color: '#93C5FD' }}>Nakshatra:</strong> {activeDayData.moon_nakshatra}</p>
            <p data-testid="active-day-daylord"><strong style={{ color: '#93C5FD' }}>Day Lord:</strong> {activeDayData.weekday_lord}</p>
            <p data-testid="active-day-rahu-kaal"><strong style={{ color: '#93C5FD' }}>Rahu Kaal:</strong> <span style={{ color: '#FCA5A5' }}>{activeDayData.rahu_kaal}</span></p>
            <p data-testid="active-day-personal-number"><strong style={{ color: '#93C5FD' }}>Personal Day:</strong> {activeDayData.personal_day_number}</p>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem', marginTop: '0.75rem' }}>
            {(activeDayData.important_markers || []).length === 0 ? (
              <span data-testid="important-marker-none" style={{ fontSize: '0.78rem', color: '#BFDBFE' }}>No lunar data available.</span>
            ) : (
              (activeDayData.important_markers || []).map((m, idx) => {
                const isPaksha = m.includes('Paksha');
                const isSpecial = m.includes('Purnima') || m.includes('Amavasya') || m.includes('Ekadashi');
                const bgColor = isPaksha ? 'rgba(250,204,21,0.15)' : isSpecial ? 'rgba(239,68,68,0.18)' : 'rgba(96,165,250,0.18)';
                const borderColor = isPaksha ? 'rgba(250,204,21,0.5)' : isSpecial ? 'rgba(239,68,68,0.5)' : 'rgba(96,165,250,0.5)';
                const textColor = isPaksha ? '#FDE68A' : isSpecial ? '#FCA5A5' : '#DBEAFE';
                return (
                  <span key={`${m}-${idx}`} data-testid={`important-marker-${idx}`} style={{ fontSize: '0.76rem', border: `1px solid ${borderColor}`, background: bgColor, borderRadius: '999px', padding: '0.2rem 0.55rem', color: textColor, fontWeight: isPaksha ? 600 : 400 }}>
                    {isPaksha && '☽ '}{m}
                  </span>
                );
              })
            )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.45rem', marginTop: '0.75rem' }}>
            {(activeDayData.planetary_positions || []).map((p, idx) => (
              <span key={`${p.planet}-${idx}`} data-testid={`planet-position-${idx}`} style={{ fontSize: '0.75rem', border: '1px solid rgba(148,163,184,0.35)', background: 'rgba(30,41,59,0.55)', borderRadius: '999px', padding: '0.2rem 0.55rem', color: '#CBD5E1' }}>
                {p.planet}: {p.sign.split(' ')[0]} {p.degree}°
              </span>
            ))}
          </div>

          <div data-testid="nakshatra-actions" style={{ marginTop: '0.85rem', border: '1px solid rgba(59,130,246,0.22)', background: 'rgba(30,64,175,0.10)', borderRadius: '10px', padding: '0.7rem' }}>
            <p style={{ color: '#BFDBFE', fontSize: '0.82rem', fontWeight: 700, marginBottom: '0.45rem' }}>
              Nakshatra Guidance for {activeDayData.moon_nakshatra}
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(260px,1fr))', gap: '0.6rem' }}>
              <div>
                <p style={{ color: '#86EFAC', fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.25rem' }}>Can Do</p>
                <ul data-testid="nakshatra-can-do" style={{ margin: 0, paddingLeft: '1rem', color: '#DCFCE7', fontSize: '0.82rem', lineHeight: 1.55 }}>
                  {(activeDayData.nakshatra_actions?.can_do || []).slice(0, 3).map((item, idx) => (
                    <li key={`can-do-${idx}`}>{item}</li>
                  ))}
                  {(activeDayData.nakshatra_actions?.can_do || []).length === 0 && (
                    <li>KB has no explicit “can-do” instruction for this nakshatra in current retrieval.</li>
                  )}
                </ul>
              </div>
              <div>
                <p style={{ color: '#FCA5A5', fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.25rem' }}>Avoid</p>
                <ul data-testid="nakshatra-avoid" style={{ margin: 0, paddingLeft: '1rem', color: '#FEE2E2', fontSize: '0.82rem', lineHeight: 1.55 }}>
                  {(activeDayData.nakshatra_actions?.avoid || []).slice(0, 3).map((item, idx) => (
                    <li key={`avoid-${idx}`}>{item}</li>
                  ))}
                  {(activeDayData.nakshatra_actions?.avoid || []).length === 0 && (
                    <li>KB has no explicit “avoid” instruction for this nakshatra in current retrieval.</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div data-testid="active-day-priority-alert" style={{ border: `1px solid ${riskColor}66`, background: `${riskColor}12`, borderRadius: '12px', padding: '0.85rem 1rem', marginBottom: '0.9rem' }}>
          <p style={{ color: riskColor, fontSize: '0.8rem', fontWeight: 700, marginBottom: '0.2rem' }}>Priority Alert</p>
          <p style={{ color: '#E5E7EB', lineHeight: 1.6 }}>{guidance.priority_alert || 'Proceed with awareness and remedy discipline.'}</p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '0.7rem', marginBottom: '1rem' }}>
          {DAILY_ESSENTIAL_KEYS.map(({ key, label }) => {
            const section = guidance[key] || {};
            return (
              <div key={key} data-testid={`daily-essential-${key}`} style={{ border: '1px solid rgba(96,165,250,0.28)', background: 'rgba(30,58,138,0.10)', borderRadius: '12px', padding: '0.85rem' }}>
                <p style={{ color: '#BFDBFE', fontSize: '0.82rem', fontWeight: 700, marginBottom: '0.3rem' }}>{label}</p>
                <p style={{ color: '#86EFAC', fontSize: '0.88rem', lineHeight: 1.6 }}><strong>Do:</strong> {section.do || 'Follow positive routine correction.'}</p>
                <p style={{ color: '#FCA5A5', fontSize: '0.88rem', lineHeight: 1.6 }}><strong>Avoid:</strong> {section.warning || 'Avoid avoidable risk choices.'}</p>
              </div>
            );
          })}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(250px,1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
          {Object.entries(AREA_META).map(([areaKey, meta]) => {
            const dayArea = activeDayData.areas?.[areaKey] || {};
            const tone = SCORE_TONE[dayArea.severity] || SCORE_TONE.NEUTRAL;
            const section = guidance[meta.key] || {};
            const Icon = meta.icon;

            return (
              <div key={areaKey} data-testid={`life-area-${meta.key}`} style={{ border: `1px solid ${tone.color}55`, background: tone.bg, borderRadius: '12px', padding: '0.9rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.45rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
                    <Icon size={15} color={tone.color} />
                    <p style={{ color: '#E5E7EB', fontSize: '0.92rem', fontWeight: 700 }}>{meta.label}</p>
                  </div>
                  <span data-testid={`life-area-${meta.key}-tone`} style={{ fontSize: '0.72rem', color: tone.color, fontWeight: 700 }}>{tone.label}</span>
                </div>
                <p style={{ color: '#93C5FD', lineHeight: 1.6, fontSize: '0.85rem' }}><strong>Neutral View:</strong> {section.focus || 'Stay structured and disciplined.'}</p>
                <p style={{ color: dayArea.severity === 'DANGER' ? '#F87171' : '#FBBF24', lineHeight: 1.6, fontSize: '0.85rem' }}><strong>Warning:</strong> {section.warning || 'Avoid rushed choices.'}</p>
                <p style={{ color: '#86EFAC', lineHeight: 1.6, fontSize: '0.85rem' }}><strong>Remedy:</strong> {section.remedy || 'Follow practical daily remedies.'}</p>
                {dayArea.severity === 'DANGER' && <AlertTriangle size={14} color="#EF4444" style={{ marginTop: '0.4rem' }} />}
              </div>
            );
          })}
        </div>

        <div data-testid="advance-watch-in-day-tab" style={{ border: '1px solid rgba(245,158,11,0.32)', background: 'rgba(245,158,11,0.10)', borderRadius: '12px', padding: '0.9rem', marginBottom: '1.1rem' }}>
          <p style={{ color: '#FCD34D', fontWeight: 700, fontSize: '0.9rem', marginBottom: '0.45rem' }}>Advance Watch (Next Months)</p>
          {(activeDayData.advance_watch || []).length === 0 ? (
            <p data-testid="advance-watch-empty" style={{ color: '#D1D5DB', fontSize: '0.84rem' }}>No major long-horizon alert mapped for this weekday.</p>
          ) : (
            <div style={{ display: 'grid', gap: '0.45rem' }}>
              {(activeDayData.advance_watch || []).map((item, idx) => (
                <div key={`${item.area}-${idx}`} data-testid={`advance-watch-item-${idx}`} style={{ border: '1px solid rgba(245,158,11,0.28)', borderRadius: '9px', padding: '0.6rem' }}>
                  <p style={{ fontSize: '0.8rem', color: '#FDE68A', fontWeight: 700 }}>
                    {item.area} · {item.risk_level} · {item.horizon_days}-Day Watch
                  </p>
                  <p style={{ color: '#FEE2E2', fontSize: '0.84rem', lineHeight: 1.55 }}>{item.warning}</p>
                  <p style={{ color: '#86EFAC', fontSize: '0.84rem', lineHeight: 1.55 }}><strong>Remedy:</strong> {item.remedy}</p>
                  {item.vulnerability && (
                    <p style={{ color: '#FBBF24', fontSize: '0.82rem', lineHeight: 1.5 }}><strong>Body Watch:</strong> {item.vulnerability}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <button data-testid="refresh-weekly-forecast" onClick={generateReport} className="btn btn-ghost" style={{ display: 'inline-flex', gap: '0.5rem', alignItems: 'center' }}>
            <RefreshCw size={14} /> Refresh Forecast
          </button>
        </div>
      </div>
    </div>
  );
}
